import React, {createRef, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import axios from "axios";
import moment from "moment";
import __ from "lodash";
import MaterialTable from "@material-table/core";
import { TablePagination, Select, MenuItem, useTheme } from "@material-ui/core";
import fileDialog from "file-dialog";
import readFileAsync from "../../../functions/ReadFileAsync";


import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import {
  MaskedText,
  DatePicker, Alert,
} from "../../../controls";
import { useDocumentTemplates, useAddDocumentTemplate, useUpdateDocumentTemplate, useDeleteDocumentTemplate, useComboValues } from "../../../services/settings";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import DocumentTemplatesValidator from "../validators/DocumentTemplatesValidator";
import { defaultComponents } from '../../../controls/Material-Table-Components';

const apiSitePrefix = getApiSitePrefix();
const dateFormat = "DD/MM/YYYY";
const today = moment().toISOString();


const DocumentTemplateList = (_props) => {
  const theme = useTheme();
  const {isAuthenticated} = useAuth0();
  const validatorColumn = ValidatorColumn(DocumentTemplatesValidator, true);

  const props = {
    ..._props
  }
  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });
  const [alert, setAlert] = useState({
    open: false,
    severity: "error",
    message: ""
  });

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddDocumentTemplate = useAddDocumentTemplate();
  const handleUpdateDocumentTemplate = useUpdateDocumentTemplate();
  const handleDeleteDocumentTemplate = useDeleteDocumentTemplate();

  const queryClient = useQueryClient();
  const extraKeys = [];

  const documentTemplates = useDocumentTemplates({tableRef, queryClient, extraKeys});
  const comboValues = useComboValues({queryClient, extraKeys: ['DOCUMENT TEMPLATE TYPES'], useCache: true});

  const getColumns = (props) => { 
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={ 
      "U": "[A-Z]", 
      "A": "[A-Z0-9]", 
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };
    //[{"code":"CHARTER_CONTRACT","value":"Charter Contract"},{"code":"SGHA_CONTRACT","value":"Standard Ground Handling Agreement"},{"code":"OTHER","value":"Other Document"}];

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        title: "Template Name",
        field: "templateName",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(400)),
        title: "Template Type",
        field: "templateType",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "",
        editComponent: (props) => {
          return (
            <Select
              value={props.value == null?'':props.value}
              onChange={
                (evt, data) => {
                  if (data.props && data.props.children && Array.isArray(data.props.children)) {
                    const typeCode = data.props.children[0];
                    props.onChange(typeCode);
                  }
                }
              }
              renderValue={(value) => {
                const values = comboValues && Array.isArray(comboValues.data)?comboValues.data:[];
                const valueIndex = __.findIndex(values, {code: value});
                return(`${valueIndex < 0 ? '' : values[valueIndex].value}`);
              }}
            >
              {(comboValues && Array.isArray(comboValues.data)) ? 
                comboValues.data.map((record) => {
                    return (
                      <MenuItem key={record.code} value={record.value}>{record.code} - {record.value}</MenuItem>
                    );
                }) :
                null
              }
            </Select>
          )
        },
        render: rowData => {
          const values = comboValues && Array.isArray(comboValues.data)?comboValues.data:[];
          const valueIndex = __.findIndex(values, {code: rowData.templateType});
          return(<div>{valueIndex < 0 ? '' : values[valueIndex].value}</div>);
        }
      },
      {
        ...baseColumn,
        ...(ColumnWidth(250)),
        title: "Revision",
        field: "revision",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(10)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        title: "Template Date",
        field: "templateDate",
        type: "date",
        emptyValue: today,
        initialEditValue:today,
        editComponent: (props) => (
            <DatePicker
              // minDate={today}
              value={props.value}
              disableToolbar={true}
              autoOk={true}
              format={dateFormat}
              onChange={props.onChange}
            />
        )
      },
    ];
  };

  const downloadTemplate = (templateFile) => {
    window.open(templateFile, "_blank");
  }

  const uploadTemplateToS3 = async (templateUuid, fileName, file) => {
    const fileContent = await readFileAsync(file);
    const buffer = new Uint8Array(fileContent);
    const dataArray = [];
    buffer.forEach((x,i)=>{dataArray[i] = x});

    const headers = {...axios.defaults.headers, 'Content-Type': 'application/json'};

    try {
      const response = await axios.post(
        `${apiSitePrefix}/documenttemplates/upload/${templateUuid}/${fileName}`,
        { document: JSON.stringify(dataArray) },
        {
          headers,
        }
      );

      //console.error(response);
      if (response && response.data) {
        if (response.data.status) {
          setAlert({ severity: "info", open: true, message: "Upload completed successfully", timeOut: 10 });
          queryClient.invalidateQueries(['DocumentTemplates', ...extraKeys]);
        } else {
          setAlert({ severity: "error", open: true, message: "The Template file failed to upload with this error: " + response.data.error });
        }
      } else {
        setAlert({ severity: "error", open: true, message: "The Template file failed to upload due to an unknown error " });
      }
    } catch (err) {
      console.error(err);
      setAlert({ severity: "error", open: true, message: "The Template file failed to upload with this error: " + err.message });
    }
  }
  const uploadTemplate = async (templateUuid) => {

    try {
      const files = await fileDialog({ accept: '.xlsx, .docx, .pdf, .html'});
      if (files && files.length > 0) {
        const fileName = files[0].name;
        // console.log("selected file: ", fileName);
        uploadTemplateToS3(templateUuid, fileName, files[0]);
      }
    } catch (err) {
      setAlert({ severity: "error", open: true, message: "Failed to upload file!" });
    }
  };

  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
        <Alert
          onChange={(open) => {
            setAlert({ ...alert, open })
          }}
          open={alert.open}
          severity={alert.severity}
          message={alert.message}
          timeOut={alert.timeOut || 0}
        />
        <MaterialTable
            tableRef={tableRef}
            style={{
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: 10,
              detailPanelType: "single",
            }}
            actions={[
              (rowData) => (
                {
                  hidden: false,
                  disabled: !state.canEdit,
                  icon: 'publish',
                  iconProps: { style: (state.canEdit) ? { color: theme.palette.primary.main } : {color: '#b3b0b0'}},
                  tooltip: "Upload Template File",
                  isFreeAction: false,
                  onClick: (e, rowData) => {
                    uploadTemplate(rowData.templateUuid);
                  }
                }
              ),
              (rowData) => (
                {
                  hidden: false,
                  disabled: !state.canEdit || !rowData.templateFile,
                  icon: 'get_app',
                  iconProps: { style: (state.canEdit && rowData.templateFile) ? { color: theme.palette.primary.main } : {color: '#b3b0b0'}},
                  tooltip: "Download Template File" + (!rowData.templateFile ? " - Template file wasn't uploaded yet" : ""),
                  isFreeAction: false,
                  onClick: (e, rowData) => {
                    downloadTemplate(rowData.templateFile);
                  }
                }
              ),
            ]}
            components={{
              ...defaultComponents,
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
            }}
            title="Document Template Management"
            columns={getColumns({canEdit: state.canEdit, comboValues})}
            data={documentTemplates.data}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                  ? (newData) => new Promise((resolve, reject) => {
                    handleAddDocumentTemplate.mutate({record: newData, resolve, reject, extraKeys});
                  })
                  : undefined,
              onRowUpdate: (newData, oldData) =>
                  new Promise((resolve, reject) => {
                    handleUpdateDocumentTemplate.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
                  }),
              onRowDelete: (oldData) =>
                  new Promise((resolve, reject) => {
                    handleDeleteDocumentTemplate.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                  }),
            }}
        />
      </div>
  );

}

export default DocumentTemplateList;
